import { differenceInCalendarDays } from 'date-fns';
import { collectClientRequests, isPendingStatus } from './vacationClientPeriods';
import type { ClientRequestCandidate } from './vacationClientPeriods';
import type { VacationDpRow } from '../models/vacationDpBoard.types';
import type { TeamVacationOverlap, ClientVacationGroup, ClientVacationPeriod, ClientVacationSegment } from '../models/vacationClientView.types';

export const TEAM_PROXIMITY_DAYS = 7;

export function hasPendingConflict(period: Pick<ClientVacationPeriod, 'isPending' | 'overlaps'>): boolean {
    return period.isPending && period.overlaps.some((overlap) => isPendingStatus(overlap.colleagueStatus));
}

export function periodOverlap(
    start1: Date,
    end1: Date,
    start2: Date,
    end2: Date
): number {
    const range = overlapRange(start1, end1, start2, end2);
    return range ? range.days : 0;
}

export function overlapRange(
    start1: Date,
    end1: Date,
    start2: Date,
    end2: Date
): { start: Date; end: Date; days: number } | null {
    const rangeStart = start1 > start2 ? start1 : start2;
    const rangeEnd = end1 < end2 ? end1 : end2;

    if (rangeStart <= rangeEnd) {
        return {
            start: rangeStart,
            end: rangeEnd,
            days: differenceInCalendarDays(rangeEnd, rangeStart) + 1,
        };
    }
    return null;
}

export function isNearby(
    end1: Date,
    start2: Date,
    proximityDays: number = TEAM_PROXIMITY_DAYS
): boolean {
    const daysBetween = differenceInCalendarDays(start2, end1);
    return daysBetween >= 0 && daysBetween <= proximityDays;
}

/**
 * Overlaps de um trecho contra os colegas do mesmo **projeto**.
 *
 * A comparação é assimétrica: um trecho **aprovado** (Scheduled/InProgress) só compara contra
 * trechos aprovados dos colegas — quem agendou primeiro não recebe aviso por causa de um pedido
 * que chegou depois e ainda pode ser recusado. Um trecho **pendente** compara contra tudo (aprovado
 * e pendente), porque o DP precisa perceber o conflito antes de decidir.
 */
function buildSegmentOverlaps(
    segment: ClientVacationSegment,
    ourRequestIsPending: boolean,
    row: VacationDpRow,
    people: VacationDpRow[],
    candidatesByCollaborator: Map<string, ClientRequestCandidate[]>
): TeamVacationOverlap[] {
    const segmentOrder = segment.order;
    const overlaps: TeamVacationOverlap[] = [];

    people.forEach((other) => {
        if (other.collaboratorId === row.collaboratorId) return;

        const otherCandidates = candidatesByCollaborator.get(other.collaboratorId) ?? [];

        otherCandidates.forEach((candidate) => {
            // Aprovado não enxerga pendente do colega; pendente enxerga tudo.
            if (candidate.isPending && !ourRequestIsPending) return;

            candidate.segments.forEach((otherSegment) => {
                const otherStart = otherSegment.startDate;
                const otherEnd = otherSegment.endDate;

                const range = overlapRange(segment.startDate, segment.endDate, otherStart, otherEnd);
                const isOverlap = !!range;
                const isBeforeNearby = isNearby(otherEnd, segment.startDate);
                const isAfterNearby = isNearby(segment.endDate, otherStart);

                if (!isOverlap && !isBeforeNearby && !isAfterNearby) return;

                let direction: 'before' | 'after' | null = null;
                let gapDays = 0;

                if (isOverlap) {
                    direction = null;
                } else if (isBeforeNearby) {
                    direction = 'after';
                    gapDays = differenceInCalendarDays(segment.startDate, otherEnd);
                } else if (isAfterNearby) {
                    direction = 'before';
                    gapDays = differenceInCalendarDays(otherStart, segment.endDate);
                }

                const newOverlapDays = isOverlap ? (range?.days ?? 0) : 0;
                const newOverlap: TeamVacationOverlap = {
                    segmentOrder,
                    collaboratorId: other.collaboratorId,
                    collaboratorName: other.collaboratorName,
                    proximity: isOverlap ? 'overlap' : 'nearby',
                    overlapDays: newOverlapDays,
                    overlapStart: isOverlap ? (range?.start ?? null) : null,
                    overlapEnd: isOverlap ? (range?.end ?? null) : null,
                    gapDays,
                    direction,
                    colleagueStart: otherStart,
                    colleagueEnd: otherEnd,
                    colleagueStatus: candidate.status,
                };

                overlaps.push(newOverlap);
            });
        });
    });

    return overlaps;
}

function getResponsibleKey(responsibleId: string | null | undefined): string {
    if (responsibleId) return responsibleId;
    return 'sem-responsavel';
}

function getResponsibleName(responsibleName: string | null | undefined): string {
    return responsibleName?.trim() || 'Sem responsável definido';
}

export function getClientResponsibleKeys(
    row: VacationDpRow,
    normalizedClientName: string
): Set<string> {
    if (!row.clientResponsibles || row.clientResponsibles.length === 0) {
        return new Set(['sem-responsavel']);
    }

    const responsiblesForClient = row.clientResponsibles.filter(
        cr => cr.clientName.toLowerCase().trim() === normalizedClientName
    );

    if (responsiblesForClient.length === 0) {
        return new Set(['sem-responsavel']);
    }

    return new Set(responsiblesForClient.map(r => getResponsibleKey(r.responsibleId)));
}

export function findTeamVacationOverlaps(
    rows: VacationDpRow[]
): ClientVacationGroup[] {
    const groupedByClient = new Map<string, VacationDpRow[]>();

    rows.forEach((row) => {
        const hasClientCandidates = collectClientRequests(row).length > 0;
        if (!hasClientCandidates) return;

        const clientNames = row.clientNames || [];

        clientNames.forEach((clientName: string) => {
            const normalizedName = clientName.toLowerCase().trim();
            if (!groupedByClient.has(normalizedName)) {
                groupedByClient.set(normalizedName, []);
            }
            groupedByClient.get(normalizedName)!.push(row);
        });
    });

    const result: ClientVacationGroup[] = [];

    groupedByClient.forEach((people, normalizedClientName) => {
        const periods: ClientVacationPeriod[] = [];
        const clientName = people[0]?.clientNames?.find(c => c.toLowerCase().trim() === normalizedClientName) || normalizedClientName;

        const candidatesByCollaborator = new Map<string, ClientRequestCandidate[]>();
        people.forEach((row) => {
            candidatesByCollaborator.set(row.collaboratorId, collectClientRequests(row));
        });

        people.forEach((row) => {
            const candidates = candidatesByCollaborator.get(row.collaboratorId) ?? [];

            candidates.forEach((candidate) => {
                const { segments } = candidate;
                if (segments.length === 0) return;

                const firstSegment = segments[0];
                const lastSegment = segments[segments.length - 1];
                const totalDays = segments.reduce((sum, seg) => sum + seg.days, 0);

                const allOverlaps: TeamVacationOverlap[] = [];
                segments.forEach((segment) => {
                    const segmentOverlaps = buildSegmentOverlaps(
                        segment,
                        candidate.isPending,
                        row,
                        people,
                        candidatesByCollaborator
                    );
                    allOverlaps.push(...segmentOverlaps);
                });

                periods.push({
                    requestId: candidate.requestId,
                    collaboratorId: row.collaboratorId,
                    collaboratorName: row.collaboratorName,
                    collaboratorPhotoUrl: row.collaboratorPhotoUrl ?? null,
                    segments,
                    startDate: firstSegment.startDate,
                    endDate: lastSegment.endDate,
                    totalDays,
                    modality: candidate.modality,
                    status: candidate.status,
                    isPending: candidate.isPending,
                    overlaps: allOverlaps,
                    requestedAt: candidate.requestedAt,
                });
            });
        });

        periods.sort((a, b) => {
            const dateCompare = a.startDate.getTime() - b.startDate.getTime();
            if (dateCompare !== 0) return dateCompare;
            return a.collaboratorName.localeCompare(b.collaboratorName, 'pt-BR');
        });

        const responsiblesMap = new Map<string, { periods: ClientVacationPeriod[]; responsibleName: string }>();

        periods.forEach((period) => {
            const row = people.find(p => p.collaboratorId === period.collaboratorId);
            if (!row) return;

            const responsibleKeys = getClientResponsibleKeys(row, normalizedClientName);
            const responsiblesForClient = row.clientResponsibles?.filter(
                cr => cr.clientName.toLowerCase().trim() === normalizedClientName
            ) ?? [];

            responsibleKeys.forEach(key => {
                if (!responsiblesMap.has(key)) {
                    const responsible = responsiblesForClient.find(r => getResponsibleKey(r.responsibleId) === key);
                    const name = responsible ? getResponsibleName(responsible.responsibleName) : 'Sem responsável definido';
                    responsiblesMap.set(key, { periods: [], responsibleName: name });
                }
                responsiblesMap.get(key)!.periods.push(period);
            });
        });

        const responsibles = Array.from(responsiblesMap.entries())
            .map(([responsibleKey, data]) => {
                const memberIds = new Set(data.periods.map(p => p.collaboratorId));

                const scopedPeriods = data.periods.map(period => ({
                    ...period,
                    overlaps: period.overlaps.filter(overlap => memberIds.has(overlap.collaboratorId)),
                }));

                const distinctPeople = new Set(scopedPeriods.map(p => p.collaboratorId)).size;
                const overlapCount = scopedPeriods.filter(hasPendingConflict).length;
                const pendingCount = scopedPeriods.filter(p => p.isPending).length;

                return {
                    responsibleKey,
                    responsibleName: data.responsibleName,
                    periods: scopedPeriods,
                    totalPeople: distinctPeople,
                    overlapCount,
                    pendingCount,
                };
            })
            .sort((a, b) => {
                if (a.overlapCount !== b.overlapCount) return b.overlapCount - a.overlapCount;
                return a.responsibleName.localeCompare(b.responsibleName, 'pt-BR');
            });

        const allDistinctPeople = new Set(people.map(p => p.collaboratorId)).size;
        const allOverlapCount = responsibles.reduce((sum, r) => sum + r.overlapCount, 0);
        const allPendingCount = responsibles.reduce((sum, r) => sum + r.pendingCount, 0);

        result.push({
            clientId: normalizedClientName,
            clientName,
            responsibles,
            totalPeople: allDistinctPeople,
            overlapCount: allOverlapCount,
            pendingCount: allPendingCount,
        });
    });

    return result.sort((a, b) => a.clientName.localeCompare(b.clientName, 'pt-BR'));
}
